/**
 * triagePathways.js
 * -----------------
 * Per-pathway triage helpers (C8). A combined ('both') referral is triaged on
 * the ADHD and autism pathways independently: each pathway has its own lock,
 * reviews and outcome-letter documents under `triagePathways.{pathway}`, so the
 * ADHD and autism letters coexist and can be sent separately (see
 * `_docs_field_for` in rms_documents.py). Single-concern referrals keep the
 * legacy top-level `triage` / `triageReviews` / `triageDocuments`.
 *
 * Mirrors the backend model in python-functions/rms_functions/rms_forms.py and
 * rms_documents.py — keep in sync.
 */

import { INTAKE_CONCERN } from './specialismMatch';

export const PATHWAY = { ADHD: 'adhd', ASD: 'asd' };

export const PATHWAY_LABELS = {
  [PATHWAY.ADHD]: 'ADHD',
  [PATHWAY.ASD]: 'Autism',
};

export const PATHWAY_TEAM_LABELS = {
  [PATHWAY.ADHD]: 'ADHD team',
  [PATHWAY.ASD]: 'Autism team',
};

/** Is this a combined (ASD + ADHD) referral, triaged on two pathways? */
export function isCombinedConcern(intakeConcern) {
  return String(intakeConcern || '').trim().toLowerCase() === INTAKE_CONCERN.BOTH;
}

/**
 * Pathways a referral is triaged on, from its intake concern.
 * 'adhd' -> ['adhd']; 'asd' -> ['asd']; 'both' -> ['adhd','asd']; unknown -> [].
 */
export function pathwaysForConcern(intakeConcern) {
  const c = String(intakeConcern || '').trim().toLowerCase();
  if (c === INTAKE_CONCERN.BOTH) return [PATHWAY.ADHD, PATHWAY.ASD];
  if (c === INTAKE_CONCERN.ADHD) return [PATHWAY.ADHD];
  if (c === INTAKE_CONCERN.ASD) return [PATHWAY.ASD];
  return [];
}

/**
 * A clear ADHD / Autism label for a referral's intake concern, for display
 * wherever a referral is listed (TD-142). Combined referrals show both
 * pathways. Returns '' when the concern is not yet classified, so an
 * unclassified referral is never mislabelled.
 *
 * 'adhd' -> 'ADHD'; 'asd' -> 'Autism'; 'both' -> 'ADHD + Autism'; unknown -> ''.
 */
export function getConcernPathwayLabel(intakeConcern) {
  return pathwaysForConcern(intakeConcern)
    .map((p) => PATHWAY_LABELS[p])
    .join(' + ');
}

/**
 * Pathway tabs a clinician should see, from their specialism.
 * 'adhd' -> ['adhd']; 'asd' -> ['asd']; 'both'/admin/support/unset -> both.
 */
export function visiblePathways(specialism) {
  const s = String(specialism || '').trim().toLowerCase();
  if (s === PATHWAY.ADHD) return [PATHWAY.ADHD];
  if (s === PATHWAY.ASD) return [PATHWAY.ASD];
  return [PATHWAY.ADHD, PATHWAY.ASD];
}

/**
 * Whether a patient belongs in a pathway's condition tab. Combined patients
 * appear in both; pure patients in their one. Pre-classification referrals (no
 * concern yet) belong in neither condition tab — they're surfaced outside the
 * tabs (the dashboard only applies this filter when the tabs are shown).
 */
export function patientInPathway(patient, pathway) {
  return pathwaysForConcern(patient?.intakeConcern).includes(pathway);
}

/** The triage lock/status object for a pathway (per-pathway for combined). */
export function pathwayTriage(patient, pathway) {
  if (isCombinedConcern(patient?.intakeConcern)) {
    const tp = (patient?.triagePathways || {})[pathway];
    if (tp && Object.keys(tp).length) return tp;
    // Back-compat: combined referrals decided before the per-pathway split keep
    // their lock/status in the legacy top-level `triage` (mirrors the
    // pathwayReviews fallback below) — without it a pre-split accepted referral
    // pairs legacy reviews with an empty lock and shows the wrong status flags.
    return patient?.triage || {};
  }
  return patient?.triage || {};
}

/**
 * The Firestore field path holding a pathway's triage outcome documents.
 * Mirrors `_docs_field_for` in rms_documents.py: combined referrals store each
 * pathway's letter under `triagePathways.{pathway}.triageDocuments` (so the ADHD
 * and autism letters coexist); single-concern referrals use the top-level
 * `triageDocuments`.
 */
export function triageDocsField(intakeConcern, pathway) {
  if (isCombinedConcern(intakeConcern) && (pathway === PATHWAY.ADHD || pathway === PATHWAY.ASD)) {
    return `triagePathways.${pathway}.triageDocuments`;
  }
  return 'triageDocuments';
}

/**
 * The triage outcome documents container for a pathway — per-pathway for
 * combined referrals, else the legacy top-level container. Returns null when
 * that pathway has no letter yet.
 */
export function pathwayTriageDocuments(patient, pathway) {
  if (isCombinedConcern(patient?.intakeConcern) && (pathway === PATHWAY.ADHD || pathway === PATHWAY.ASD)) {
    return ((patient?.triagePathways || {})[pathway] || {}).triageDocuments || null;
  }
  return patient?.triageDocuments || null;
}

/**
 * The admin handover container for a pathway (TDI-386) — the admin team's
 * saved EMIS-filing progress, written by rmsSetAdminHandoverTick. Stored per
 * pathway for combined referrals (each pathway is handed over separately),
 * else top-level. Mirrors `_handover_field_parts` in rms_admin_handover.py —
 * keep in sync. Returns null when nothing has been recorded.
 */
export function pathwayAdminHandover(patient, pathway) {
  if (isCombinedConcern(patient?.intakeConcern) && (pathway === PATHWAY.ADHD || pathway === PATHWAY.ASD)) {
    return ((patient?.triagePathways || {})[pathway] || {}).adminHandover || null;
  }
  return patient?.adminHandover || null;
}

/** The triage reviews array for a pathway (per-pathway for combined). */
export function pathwayReviews(patient, pathway) {
  if (isCombinedConcern(patient?.intakeConcern)) {
    const tp = (patient?.triagePathways || {})[pathway] || {};
    if (Array.isArray(tp.reviews) && tp.reviews.length) return tp.reviews;
    // Back-compat: combined referrals decided before the per-pathway split (or
    // seeded with a single shared decision) keep their reviews in the legacy
    // top-level `triageReviews`. Fall back to those so such patients are still
    // classified by their real decisions instead of reading as not-yet-triaged.
    return Array.isArray(patient?.triageReviews) ? patient.triageReviews : [];
  }
  return Array.isArray(patient?.triageReviews) ? patient.triageReviews : [];
}

/**
 * Is this referral someone else's to finish?
 *
 * A clinician's triage queue hides referrals another clinician has taken, so a
 * later reviewer is never biased by who is (or was) working a case (LSC-040).
 * The test is the ASSIGNMENT, not the lock status: a referral whose decision is
 * recorded but whose outcome letter is not yet signed off stays in Pending
 * Review and keeps its assignee (Tom King, 29 Sept), so reading the status
 * would hand every clinician's unfinished work back to everyone else.
 *
 * A first autism rejection clears the assignee deliberately, so a referral
 * awaiting a second opinion belongs to nobody and stays visible to all.
 *
 * @param {Object} patient
 * @param {string} pathway
 * @param {string} clinicianId - the viewer
 * @returns {boolean}
 */
export function isAssignedToAnotherClinician(patient, pathway, clinicianId) {
  const assignee = pathwayTriage(patient, pathway)?.assignedClinicianId;
  return Boolean(assignee) && assignee !== clinicianId;
}

/**
 * A clinician's own evidence tags for a pathway.
 *
 * Tags are per-pathway (a combined referral triages twice, independently) and
 * private to the clinician who made them: a second reviewer's list starts from
 * their own reading of the referral, not the first reviewer's. The server
 * enforces both; this is the matching read.
 *
 * There is no legacy top-level fallback for a combined referral, because there
 * are no tags older than the per-pathway field.
 *
 * @param {Object} patient
 * @param {string} pathway - 'adhd' | 'asd'
 * @param {string} clinicianId - whose tags to return; none without one
 * @returns {Array}
 */
export function pathwayEvidenceTags(patient, pathway, clinicianId) {
  if (!clinicianId) return [];
  const combined = isCombinedConcern(patient?.intakeConcern);
  const stored = combined
    ? ((patient?.triagePathways || {})[pathway] || {}).evidenceTags
    : patient?.evidenceTags;
  if (!Array.isArray(stored)) return [];
  return stored.filter((tag) => tag?.clinicianId === clinicianId);
}

/**
 * Resolve the pathway a clinician is acting on for a patient.
 * - Single-concern patient -> its one pathway (authoritative).
 * - Otherwise (combined, or not-yet-classified) the pathway is contextual: it
 *   comes from the active dashboard tab (`requestedPathway`), else the
 *   clinician's specialism when that is a single pathway. It is never asked of
 *   the clinician. Only a combined/unclassified patient triaged by a
 *   both-specialism clinician with no active tab is unresolved (-> null).
 */
export function resolveActingPathway(patient, requestedPathway, specialism) {
  const pathways = pathwaysForConcern(patient?.intakeConcern);
  if (pathways.length === 1) return pathways[0];
  const req = String(requestedPathway || '').trim().toLowerCase();
  if (req === PATHWAY.ADHD || req === PATHWAY.ASD) return req;
  const s = String(specialism || '').trim().toLowerCase();
  if (s === PATHWAY.ADHD || s === PATHWAY.ASD) return s;
  return null;
}
